/**
 * SplashPage — 랜딩페이지 껍데기 (비로그인 진입점)
 *
 * 2026-07-31 시안 교체. 상단 광고바 + 헤더는 LandingHeader로 분리했다 —
 * 시안에서는 Hero 프레임 안에 있지만, 그대로 두면 FAQ·문의 탭에서 헤더가 사라진다.
 * 헤더 외형만 시안을 따르고 탭 전환 로직은 그대로 둬서 FAQ 화면이 안 깨진다.
 *
 * landing.css의 --lp-* 변수는 .lp 스코프 안에서만 유효하다 —
 * 랜딩 색이 서비스 화면으로 새어나가지 않게 하는 경계다(D-07 §1.2 "예외의 한계").
 */
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useAuthStore } from '@indipia/shared';
import { SplashFaqPage } from './SplashFaqPage';
import { SplashIntroPage } from './SplashIntroPage';
import { SplashContactPage } from './SplashContactPage';
import { LandingHeader } from './splash/LandingHeader';
import type { LandingTab } from './splash/tabs';
import './splash/landing.css';

export function SplashPage() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<LandingTab>('소개');
  const [adOpen, setAdOpen] = useState(true);

  // 스크롤 스냅은 스크롤 컨테이너(문서 루트)에 걸어야 해서 <html>을 직접 건드린다.
  // 소개 탭에서만 켜고, 탭이 바뀌거나 화면을 벗어나면 반드시 되돌린다 —
  // 정리를 빠뜨리면 로그인·서비스 화면까지 스냅이 따라간다.
  // 조기 반환(dev 우회 리다이렉트)보다 위에 둬야 훅 호출 순서가 흔들리지 않는다.
  useEffect(() => {
    if (activeTab !== '소개') return;
    const root = document.documentElement;
    root.classList.add('lp-snap');
    return () => root.classList.remove('lp-snap');
  }, [activeTab]);

  // 인증 우회(로컬 미리보기) 중에는 '/'가 스튜디오 홈으로 바로 넘어간다.
  // 랜딩을 확인해야 할 때는 '/?landing=1'로 이 리다이렉트만 건너뛴다 — 우회 자체는 그대로 유지.
  if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true' && !searchParams.has('landing')) {
    return <Navigate to="/app" replace />;
  }

  // 토큰이 이미 있으면(재방문·랜딩 경유) 로그인 폼을 건너뛰고 바로 입장 — 실제 유효성은
  // AppFrame의 init/session 호출 + apiClient의 401 자동 refresh가 검증한다.
  const goStart  = () => void navigate(accessToken ? '/app' : '/login');
  const goSignup = () => void navigate('/signup');

  return (
    <div className={`lp flex min-h-screen flex-col bg-white ${adOpen ? '' : 'lp-ad-closed'}`}>
      <LandingHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onStart={goStart}
        onSignup={goSignup}
        adOpen={adOpen}
        onCloseAd={() => setAdOpen(false)}
      />

      <div className="flex flex-1 flex-col">
        {activeTab === '소개' && <SplashIntroPage />}

        {activeTab === 'FAQ' && <SplashFaqPage />}

        {activeTab === '문의' && <SplashContactPage />}
      </div>

      {activeTab !== '소개' && (
        <p className="border-t border-border py-8 text-center text-xs text-text-muted">
          © 2026 Indipia. All rights reserved.
        </p>
      )}
    </div>
  );
}
